import { useEffect, useRef } from 'react';

import { useTranslation } from '@/i18n/useTranslation';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { Button } from './Button';

export interface ExitConfirmDialogProps {
  /** Dismiss and put the candidate back on the screen they were on. Nothing is cleared. */
  readonly onKeepGoing: () => void;
  /** End the candidate's session and return the kiosk to Welcome. */
  readonly onExit: () => void;
}

/**
 * The confirmation `Exit` opens [Figma · `States / 01 · Overlay · Exit — clear this session?`, at Desktop ·
 * iPad · Mobile].
 *
 * **Why Exit is a question and not an action.** OQ-101, ruled by the Operator on 2026-09-10: exiting BEFORE
 * the consent modal is completed writes nothing, and exiting AFTER it leaves the Bullhorn record standing —
 * the same rule 0195 states from the other side. There is no deletion path and none was asked for. So the
 * one thing Exit can destroy is what the candidate has typed and not yet submitted, on a machine they cannot
 * log back into, and that is worth one tap to confirm.
 *
 * **The copy is ISS's, verbatim, and it says the information is SAVED** [decision 0340]. The earlier body
 * promised erasure, which [0199] showed the product never performs; ISS replaced it with one disclosure that
 * is true on both sides of the gates. It is a disclosure, not a warning, which is why the frame sets it in
 * the calm muted body style rather than anything that reads as an alarm.
 *
 * Values from the frame, identical at all three viewports: a 327-wide panel (which is exactly 375 less two
 * 24px gutters, so it is also the mobile full width), 16px radius, 24px padding, 16px between blocks, 8px
 * between the two actions, `--elevation-3`'s five stacked shadows, over a 45% black scrim.
 *
 * **Not dismissible by an outside click**, following `DifferentEmailDialog` and `ScanOverlay`: a candidate
 * resting a palm on a touchscreen must not dismiss a dialog. Escape IS bound, unlike `ScanOverlay` — there
 * every way out was consequential, and here the dismissing action is the SAFE one, so the keyboard
 * convention and the safe default point the same way.
 */
export function ExitConfirmDialog({ onKeepGoing, onExit }: ExitConfirmDialogProps) {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const panel = useRef<HTMLDivElement | null>(null);

  /*
   * A focus loop rather than a focus-trap library, the idiom `ScanOverlay` established: two buttons is a
   * small enough surface to close honestly. Escape resolves to "keep going" — the outcome that loses
   * nothing — so a stray key press can never end somebody's session.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onKeepGoing();
        return;
      }
      if (event.key !== 'Tab' || panel.current === null) return;
      const stops = [...panel.current.querySelectorAll<HTMLElement>('button')];
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;
      /*
       * `at <= 0` covers the heading as well as the first stop, as in `NeedHelpDialog`: focus lands on the
       * heading on mount and it is not itself tabbable, so Tab walks forward out of it on its own while
       * Shift+Tab would walk straight out of the dialog onto the screen behind.
       */
      const at = stops.indexOf(document.activeElement as HTMLElement);
      const backwards = event.shiftKey && at <= 0;
      const forwards = !event.shiftKey && at === stops.length - 1;
      if (!backwards && !forwards) return;
      event.preventDefault();
      (backwards ? last : first).focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeepGoing]);

  return (
    <div
      aria-labelledby="exit-confirm-title"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-6"
      role="dialog"
    >
      {/*
        `shadow:` is the TYPE HINT and it is load-bearing — see the long note in `KioskCard`. Without it
        Tailwind reads a bare `var()` in an arbitrary shadow as a COLOUR, generates `--tw-shadow-color`, and
        the panel ships flat.
      */}
      <div
        className="flex w-full max-w-[327px] flex-col gap-4 rounded-[var(--radius-lg)] bg-[var(--card)] p-6 shadow-[shadow:var(--elevation-3)]"
        ref={panel}
      >
        <h2
          className="kiosk-heading-focus w-full text-center text-base font-semibold leading-6 text-[var(--foreground)]"
          id="exit-confirm-title"
          ref={heading}
          tabIndex={-1}
        >
          {t('exit.heading')}
        </h2>
        <p className="w-full text-center text-sm leading-5 text-[var(--muted-foreground)]">
          {t('exit.body')}
        </p>
        {/*
          YES above NO, because that is the order ISS typed them in [0340] and the frame follows it. The
          frame's note on this overlay keeps the weights where they were: NO, the answer that loses nothing,
          stays the FILLED primary, and YES — which ends the session — is the outlined one. The safe choice
          is still the one that looks like the default, even though it now comes second.
        */}
        <div className="flex w-full flex-col gap-2">
          <Button fullWidth onClick={onExit} variant="outline">
            {t('exit.confirm')}
          </Button>
          <Button fullWidth onClick={onKeepGoing}>
            {t('exit.keepGoing')}
          </Button>
        </div>
      </div>
    </div>
  );
}
